// ------------------ IMPORTS -------------------

// Assets / Styles
import { Plus } from "@untitled-ui/icons-react";

// Utils / Config / Services
import { useTranslation } from "react-i18next";
import { generatePath, useParams } from "react-router";
import { useEffect, useMemo, useState } from "react";
import { pathname } from "#src/config/pathname";
import { endpointService } from "#src/services/endpoint";
import useFetchPaginated from "#src/hooks/useFetchPaginated";
import { WorkspaceMemberRoleValue } from "#src/config/filter/workspace/value";
import { useFilter } from "#src/commonV2/Table/Filter/useFilter";
import { useAppSelector } from "#src/store/hooks";
import { IPassport } from "#src/services/endpoint/passports/types";
import {
  PassportActionType,
  PassportRuleType,
} from "#src/services/endpoint/passports/enums";

// Components
import { BreadcrumbV2 } from "#src/commonV2/Breadcrumb";
import { ButtonV2 } from "#src/commonV2/Button";
import { SearchboxV2 } from "#src/commonV2/Table/Searchbox";

// Child Components
import { PassportTable } from "#src/components/endpoint/passports/PassportTable";
import DeletePassportModal from "#src/components/endpoint/passports/DeletePassportModal";
import AddEditPassportModal from "#src/components/endpoint/passports/AddEditPassportModal";
import {
  passportStatusFilterItems,
  passportActionFilterItems,
  passportPortFilterItems,
  passportTypeFilterItems,
} from "#src/components/endpoint/passports/filterItems";
import { FilterDropdownV2 } from "#src/commonV2/Table/Filter/FilterDropdown";

// Others

enum SearchParamKey {
  TYPE = "type",
  ACTION = "action",
  PORT = "port",
  STATUS = "status",
}

export const EndpointPassports = () => {
  // ------------------ HOOKS -------------------

  const { t } = useTranslation("endpoint", { keyPrefix: "passports" });
  const { workspaceId } = useParams<"workspaceId">();
  const currentWorkspace = useAppSelector(
    (state) => state.auth.currentWorkspace
  );

  // ------------------ DATA -------------------

  // Cloned passport list
  const [passportList, setPassportList] = useState<IPassport[]>([]);

  // Modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [selectedPassport, setSelectedPassport] = useState<IPassport | null>(
    null
  );

  // Search/sort
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  // Get filter items
  const { value: passportType, renderProps: passportTypeFilterRenderProps } =
    useFilter({
      label: t("filters.type.label"),
      selectionList: passportTypeFilterItems,
      options: {
        searchParamKey: SearchParamKey.TYPE,
      },
    });

  const {
    value: passportAction,
    renderProps: passportActionFilterRenderProps,
  } = useFilter({
    label: t("filters.action.label"),
    selectionList: passportActionFilterItems,
    options: {
      searchParamKey: SearchParamKey.ACTION,
    },
  });

  const { value: passportPort, renderProps: passportPortFilterRenderProps } =
    useFilter({
      label: t("filters.port.label"),
      selectionList: passportPortFilterItems,
      options: {
        searchParamKey: SearchParamKey.PORT,
      },
    });

  const {
    value: passportStatus,
    renderProps: passportStatusFilterRenderProps,
  } = useFilter({
    label: t("filters.status.label"),
    selectionList: passportStatusFilterItems,
    options: {
      searchParamKey: SearchParamKey.STATUS,
    },
  });

  // ------------------ COMPUTED -------------------

  const currentUserRole = currentWorkspace?.role;

  const canEdit =
    currentUserRole === WorkspaceMemberRoleValue.OWNER ||
    currentUserRole === WorkspaceMemberRoleValue.ADMIN;

  // Can drag and drop if user is allowed and no filter is applied
  const canDnd =
    canEdit &&
    !passportType &&
    !passportAction &&
    !passportPort &&
    !passportStatus;

  const fetchParams = useMemo<Parameters<typeof endpointService.listPassports>>(
    () => [
      workspaceId!,
      {
        q: searchKeyword,
        type: passportType as PassportRuleType,
        action: passportAction as PassportActionType,
        port: passportPort,
        enabled: passportStatus,
      },
    ],
    [
      workspaceId,
      searchKeyword,
      passportType,
      passportAction,
      passportPort,
      passportStatus,
    ]
  );

  const {
    list: pList,
    isLoading,
    // updateData: updatePassportList,
  } = useFetchPaginated(endpointService.listPassports, fetchParams);

  // ------------------ METHODS -------------------

  // ------------------ EFFECTS -------------------

  useEffect(() => {
    setPassportList(pList || []);
  }, [pList]);

  // ------------------ RENDER -------------------

  if (!workspaceId) {
    return null;
  }

  return (
    <div className="pt-5 pb-6 flex flex-col gap-5 min-h-screen">
      {/* Header (Breadcrumb + title) */}
      <div className="px-8 flex flex-col gap-4">
        <BreadcrumbV2
          levels={[
            {
              name: t("breadcrumb.levels.endpoint"),
              path: generatePath(pathname.ENDPOINT_OVERVIEW, { workspaceId }),
            },
          ]}
          currentPage={{
            name: t("breadcrumb.current.passports"),
          }}
        />
        <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
          <div>
            <h1 className="text-display-xs font-semibold text-gray-v2-900 mb-2 lg:mb-4">
              {t("title")}
            </h1>
            <p className="text-sm text-gray-v2-900">{t("desc")}</p>
          </div>

          <ButtonV2
            variant="primary"
            size="md"
            color="brand"
            onClick={() => {
              // Open add modal
              setIsAddEditModalOpen(true);
              setSelectedPassport(null);
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            {t("button.addNewPassport")}
          </ButtonV2>
        </div>
      </div>
      {/* Header (Breadcrumb + title) end */}

      <div className="px-8 flex flex-col gap-4 flex-1">
        <div className="flex justify-between items-center gap-2 flex-wrap">
          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <FilterDropdownV2 {...passportTypeFilterRenderProps} />
            <FilterDropdownV2 {...passportActionFilterRenderProps} />
            <FilterDropdownV2 {...passportPortFilterRenderProps} />
            <FilterDropdownV2 {...passportStatusFilterRenderProps} />
          </div>
          {/* Filters end */}

          <div className="flex gap-2 v2">
            {/* Searchbox */}
            <SearchboxV2
              value={searchKeyword}
              onChange={(value) => {
                setSearchKeyword(value);
              }}
              placeholder={t("table.filter.searchbox.placeholder")}
            />
            {/* Searchbox end */}
          </div>
        </div>

        {/* Table */}
        <PassportTable
          workspaceId={workspaceId}
          passportList={passportList}
          setPassportList={setPassportList}
          isLoading={isLoading}
          setIsAddEditModalOpen={setIsAddEditModalOpen}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          setSelectedPassport={setSelectedPassport}
          canDnd={canDnd}
          canEdit={canEdit}
        />
        {/* Table end */}
      </div>

      {/* Modals */}
      <AddEditPassportModal
        isOpen={isAddEditModalOpen}
        workspaceId={workspaceId}
        selectedPassport={selectedPassport}
        closeModal={() => {
          setIsAddEditModalOpen(false);
          setSelectedPassport(null);
        }}
        onEditSuccess={(newData) => {
          // Update passport list item with new data
          setPassportList((prev) =>
            prev.map((item) => (item.id === newData.id ? newData : item))
          );
        }}
        onAddSuccess={(newData) => {
          // Add new passport to the list
          setPassportList((prev) => [...prev, newData]);
        }}
      />

      <DeletePassportModal
        isOpen={isDeleteModalOpen}
        workspaceId={workspaceId}
        passportId={selectedPassport?.id || ""}
        closeModal={() => {
          setIsDeleteModalOpen(false);
          setSelectedPassport(null);
        }}
        onSuccess={(deletedPassportId) => {
          // Update passport list
          setPassportList((prev) =>
            prev.filter((item) => item.id !== deletedPassportId)
          );
        }}
      />
      {/* Modals end */}
    </div>
  );
};
